import React, { useContext, useEffect, useMemo, useState } from 'react'
import DetailsBox, {
  DetailRow,
} from '../../../../../components/Cards/DetailsBox'
import AssignIDCardForm from './AssignIDCardForm'
import API from '../../../../../services/API'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  Paper,
  IconButton,
  Grid,
} from '@mui/material'
import { GlobalContext, UserContext } from '../../../../../App'
import moment from 'moment'
import AddRounded from '@mui/icons-material/AddRounded'
import ArchiveRounded from '@mui/icons-material/ArchiveRounded'
import BadgeRounded from '@mui/icons-material/BadgeRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import Inventory from '@mui/icons-material/Inventory'
import UpgradeLock from '../../../../../assets/icons/UpgradeLock'

const IDCards = ({ employeeID }) => {
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [idCards, setIDCards] = useState([])
  const [openAddNew, setOpenAddNew] = useState(false)
  const [selectedIDCard, setSelectedIDCard] = useState(null)
  const [includeArchived, setIncludeArchived] = useState(false)
  const { setToast, tenantInfo, setUpgradeRequired } = useContext(GlobalContext)
  const { user } = useContext(UserContext)

  if (
    Number(tenantInfo?.subscriptionTier) < 2 &&
    !user?.security?.isSiteAdmin
  ) {
    return null
  } else if (
    Number(tenantInfo?.subscriptionTier < 2 && user?.security?.isSiteAdmin)
  ) {
    return (
      <Grid item xs={12} lg={6}>
        <DetailsBox
          title="ID Cards"
          noBorder
          buttonRight={
            <UpgradeLock
              onClick={() =>
                setUpgradeRequired({
                  title: 'Employee ID Cards',
                  benefits: ['Record ID Card Allocation'],
                })
              }
            />
          }
        />
      </Grid>
    )
  }

  const fetchIDCards = () => {
    setLoading(true)
    setFetchError(false)

    API.get('/employee-id-cards/', {
      params: {
        employeeID,
        includeArchived,
      },
    })
      .then((res) => {
        if (res.status === 200) {
          setIDCards(res.data)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchIDCards()
  }, [includeArchived])

  const handleArchive = () => {
    API.post('/employee-id-cards/archive/' + selectedIDCard._id, {
      alreadyArchived: selectedIDCard.archived,
    }).then((res) => {
      if (res.status === 200) {
        setToast({
          message: selectedIDCard.archived
            ? 'Archive Undone'
            : 'ID Card Archived',
        })
        setSelectedIDCard(null)
        fetchIDCards()
      }
    })
  }

  const getStatus = (card) => {
    if (card?.archived) return 'Archived'
    if (card?.noExpiry) return 'No Expiry'
    if (moment(card?.expiry).isBefore(moment(), 'day')) return 'Expired'
    return 'Active'
  }

  const getStatusColor = (card) => {
    const status = getStatus(card)
    if (status === 'Archived') return 'default'
    if (status === 'Expired') return 'error'
    if (status === 'No Expiry') return 'info'
    return 'success'
  }

  const sortedCards = useMemo(() => {
    return [...idCards].sort(
      (a, b) => moment(b.issueDate).valueOf() - moment(a.issueDate).valueOf(),
    )
  }, [idCards])

  return (
    <Grid item xs={12} lg={6}>
      <DetailsBox
        title="ID Cards"
        noBorder
        buttonRight={
          <Stack direction="row" spacing={1}>
            {!includeArchived && (
              <Tooltip title="View archived cards">
                <Button
                  onClick={() => setIncludeArchived(true)}
                  color="anBlue"
                  size="small"
                  variant="standard"
                  startIcon={<Inventory fontSize="small" />}
                  sx={{ borderRadius: 2 }}
                >
                  Archived
                </Button>
              </Tooltip>
            )}

            {includeArchived && (
              <Button
                onClick={() => setIncludeArchived(false)}
                color="inherit"
                size="small"
                variant="standard"
                sx={{ borderRadius: 2 }}
              >
                Hide Archived
              </Button>
            )}

            <Button
              color="anPurple"
              size="small"
              onClick={() => setOpenAddNew(true)}
              variant="standard"
              startIcon={<AddRounded />}
              sx={{ borderRadius: 2 }}
            >
              Add ID Card
            </Button>
          </Stack>
        }
      >
        <Stack spacing={1.25}>
          {loading && <LinearProgress />}

          {!loading && fetchError && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 3,
                borderStyle: 'dashed',
              }}
            >
              <Typography variant="body2" color="error">
                Unable to load ID cards right now.
              </Typography>
            </Paper>
          )}

          {!loading && !fetchError && sortedCards.length === 0 && (
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: 3,
                borderStyle: 'dashed',
                textAlign: 'center',
              }}
            >
              <Stack spacing={1} alignItems="center">
                <BadgeRounded color="disabled" />
                <Typography variant="title3">No ID cards recorded</Typography>
                <Typography variant="body2" color="text.secondary">
                  Add an ID card to keep track of issue and expiry dates.
                </Typography>
              </Stack>
            </Paper>
          )}

          {!loading &&
            !fetchError &&
            sortedCards.map((card) => (
              <Paper
                key={card._id}
                variant="outlined"
                onClick={() => setSelectedIDCard(card)}
                sx={{
                  p: 1.6,
                  borderRadius: 3,
                  cursor: 'pointer',
                  transition: 'all 0.18s ease',
                  borderColor:
                    getStatus(card) === 'Expired' ? 'error.light' : 'divider',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 3,
                    borderColor: 'primary.light',
                  },
                }}
              >
                <Stack spacing={1.2}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1}
                  >
                    <Stack spacing={0.35}>
                      <Typography variant="title3" fontWeight={800}>
                        {card?.reference || 'Unnamed ID Card'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Issued by{' '}
                        {card?.issuedBy?.firstName && card?.issuedBy?.lastName
                          ? `${card.issuedBy.firstName} ${card.issuedBy.lastName}`
                          : 'Unknown'}
                      </Typography>
                    </Stack>

                    <Chip
                      label={getStatus(card)}
                      size="small"
                      color={getStatusColor(card)}
                      sx={{ borderRadius: 2, fontWeight: 700 }}
                    />
                  </Stack>

                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    <Chip
                      variant="outlined"
                      size="small"
                      label={`Issued: ${moment(card.issueDate).format(
                        'DD/MM/YYYY',
                      )}`}
                      sx={{ borderRadius: 2 }}
                    />
                    <Chip
                      variant="outlined"
                      size="small"
                      label={`Expires: ${
                        card.noExpiry
                          ? 'Never'
                          : moment(card.expiry).format('DD/MM/YYYY')
                      }`}
                      color={
                        !card.noExpiry &&
                        moment(card.expiry).isBefore(moment(), 'day')
                          ? 'error'
                          : 'default'
                      }
                      sx={{ borderRadius: 2 }}
                    />
                    {card.archived && (
                      <Chip
                        size="small"
                        label="Archived"
                        variant="filled"
                        sx={{ borderRadius: 2 }}
                      />
                    )}
                  </Stack>
                </Stack>
              </Paper>
            ))}
        </Stack>
      </DetailsBox>

      <AssignIDCardForm
        open={openAddNew}
        onClose={() => setOpenAddNew(false)}
        employeeID={employeeID}
        setIDCards={setIDCards}
      />

      <Dialog
        open={Boolean(selectedIDCard)}
        onClose={() => setSelectedIDCard(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1.2 }}>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Stack spacing={0.5}>
              <Typography variant="title2">
                {selectedIDCard?.reference || 'ID Card'}
              </Typography>
              {selectedIDCard && (
                <Chip
                  label={getStatus(selectedIDCard)}
                  size="small"
                  color={getStatusColor(selectedIDCard)}
                  sx={{ width: 'fit-content', borderRadius: 2 }}
                />
              )}
            </Stack>

            <IconButton onClick={() => setSelectedIDCard(null)} size="small">
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={1.25}>
            <DetailRow
              label="Issued"
              value={moment(selectedIDCard?.issueDate).format('DD/MM/YYYY')}
            />
            <DetailRow
              label="Issued By"
              value={
                selectedIDCard?.issuedBy?.firstName &&
                selectedIDCard?.issuedBy?.lastName
                  ? `${selectedIDCard.issuedBy.firstName} ${selectedIDCard.issuedBy.lastName}`
                  : 'Unknown'
              }
            />
            <DetailRow
              label="Expires"
              value={
                selectedIDCard?.noExpiry
                  ? 'Never'
                  : moment(selectedIDCard?.expiry).format('DD/MM/YYYY')
              }
            />
            <DetailRow
              label="Archived"
              value={selectedIDCard?.archived ? 'Yes' : 'No'}
            />
          </Stack>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button
            onClick={handleArchive}
            variant="outlined"
            color={selectedIDCard?.archived ? 'success' : 'anRed'}
            size="small"
            startIcon={<ArchiveRounded />}
            sx={{ borderRadius: 2 }}
          >
            {selectedIDCard?.archived ? 'Undo Archive' : 'Archive ID Card'}
          </Button>

          <Button
            onClick={() => setSelectedIDCard(null)}
            variant="contained"
            color="anBlue"
            size="small"
            sx={{ borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  )
}

export default IDCards
